import { useState } from 'react';
import ModalContainer from '../shared/ModalContainer';
import { useTheme } from '../../hooks/useTheme';

export default function DeleteProductModal({ isOpen, onClose, onConfirm, product }) {
  const { currentThemeType, themeTypes } = useTheme();
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const isLightTheme = currentThemeType === themeTypes.LIGHT;

  if (!product) return null;

  const isNameMatch = confirmText === product.name;

  const handleConfirm = async () => {
    if (!isNameMatch) return;

    setIsDeleting(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (isDeleting) return; // Prevent closing while deleting
    setConfirmText('');
    onClose();
  };

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={handleClose}
      title={`Delete Product: ${product.name}`}
      subtitle="This action is permanent and cannot be undone."
      maxWidth="max-w-2xl"
      isProcessing={isDeleting}
      className="border-2 border-red-500/50"
      footer={
        <div className="p-6 bg-gray-900/50 flex gap-3 justify-end">
          <button
            onClick={handleClose}
            disabled={isDeleting}
            className="px-5 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isNameMatch || isDeleting}
            className={`px-5 py-2.5 rounded-lg transition-all duration-200 font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${isLightTheme ? 'bg-gradient-to-br from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:hover:from-red-600 disabled:hover:to-red-700' : 'bg-gradient-to-br from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:hover:from-red-600 disabled:hover:to-red-700'}`}
            style={{ color: 'var(--color-text-on-accent)' }}
          >
            {isDeleting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Deleting...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Yes, I want to Delete {product.name}
              </>
            )}
          </button>
        </div>
      }
    >
      {/* Content */}
      <div className={`p-6 space-y-6 ${isLightTheme ? 'bg-gradient-to-br from-red-50 to-red-100/50' : 'bg-gradient-to-br from-red-900/10 to-red-800/5'}`} style={{ color: 'var(--color-text-on-accent)' }}>
                {/* Warning Messages */}
          <div className="space-y-4">
            <div className={`rounded-lg p-4 ${isLightTheme ? 'bg-red-100 border border-red-300' : 'bg-red-900/20 border border-red-500/50'}`}>
              <div className="flex items-start gap-3">
                <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isLightTheme ? 'text-red-700' : 'text-red-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <h4 className={`font-semibold mb-2 ${isLightTheme ? 'text-red-900' : 'text-red-300'}`}>Critical Warning</h4>
                  <p className={`text-sm leading-relaxed ${isLightTheme ? 'text-gray-800' : 'text-gray-300'}`}>
                    Deleting this product will <span className={`font-semibold ${isLightTheme ? 'text-red-800' : 'text-red-400'}`}>permanently remove ALL associated data</span>, including:
                  </p>
                </div>
              </div>
            </div>

            {/* Data Loss List */}
            <div className={`rounded-lg p-4 ${isLightTheme ? 'bg-white border border-gray-300' : 'bg-gray-900/50 border border-gray-700'}`}>
              <ul className={`space-y-2 text-sm ${isLightTheme ? 'text-gray-700' : 'text-gray-300'}`}>
                <li className="flex items-start gap-2">
                  <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isLightTheme ? 'text-red-600' : 'text-red-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span><span className={`font-semibold ${isLightTheme ? 'text-gray-900' : 'text-white'}`}>All Knowledge Collections</span> associated with this product</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isLightTheme ? 'text-red-600' : 'text-red-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span><span className={`font-semibold ${isLightTheme ? 'text-gray-900' : 'text-white'}`}>All Knowledge Items</span> within those collections</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isLightTheme ? 'text-red-600' : 'text-red-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span><span className={`font-semibold ${isLightTheme ? 'text-gray-900' : 'text-white'}`}>All User Access Permissions</span> for this product</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isLightTheme ? 'text-red-600' : 'text-red-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span><span className={`font-semibold ${isLightTheme ? 'text-gray-900' : 'text-white'}`}>All Role Assignments</span> for this product</span>
                </li>
                <li className="flex items-start gap-2">
                  <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isLightTheme ? 'text-red-600' : 'text-red-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span><span className={`font-semibold ${isLightTheme ? 'text-gray-900' : 'text-white'}`}>All Activity History</span> related to this product</span>
                </li>
              </ul>
            </div>

            {/* Final Warning */}
            <div className={`rounded-lg p-4 ${isLightTheme ? 'bg-red-50 border border-red-400' : 'bg-red-950/30 border border-red-600/50'}`}>
              <p className={`text-sm leading-relaxed ${isLightTheme ? 'text-red-900' : 'text-red-200'}`}>
                <span className={`font-semibold ${isLightTheme ? 'text-red-900' : 'text-red-300'}`}>⚠️ There is no way to recover this data after deletion.</span> Make sure you have exported or backed up any important information before proceeding.
              </p>
            </div>
          </div>

          {/* Confirmation Input */}
          <div className="space-y-3">
            <label className="block">
              <span className={`text-sm font-medium mb-2 block ${isLightTheme ? 'text-gray-700' : 'text-gray-300'}`}>
                To confirm deletion, type the product name: <span className={`font-semibold ${isLightTheme ? 'text-red-700' : 'text-red-400'}`}>{product.name}</span>
              </span>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type product name here"
                disabled={isDeleting}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                autoComplete="off"
              />
            </label>

            {confirmText && !isNameMatch && (
              <p className="text-xs text-red-400 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Product name does not match. Please type exactly: {product.name}
              </p>
            )}

            {isNameMatch && (
              <p className="text-xs text-green-400 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Product name confirmed
              </p>
            )}
          </div>
        </div>
      </ModalContainer>
  );
}
